import { useEffect, useState } from "react";
import { RichTextEditor, Link } from "@mantine/tiptap";
import { useEditor } from "@tiptap/react";
import { NodeSelection } from "@tiptap/pm/state";
import { Extension, Node, mergeAttributes } from "@tiptap/core";
import StarterKit from "@tiptap/starter-kit";
import { Markdown } from "@tiptap/markdown";
import Youtube, {
  getEmbedUrlFromYoutubeUrl,
  getAttributesFromYoutubeEmbedUrl,
} from "@tiptap/extension-youtube";
import Highlight from "@tiptap/extension-highlight";
import {
  IconBrandYoutube,
  IconBrandSoundcloud,
  IconWorld,
  IconCode,
  IconAlertTriangle,
  IconQuote,
  IconPhoto,
  IconMarkdown,
} from "@tabler/icons-react";
import { Alert, Button, Group, Modal, Select, Stack, TextInput, Textarea } from "@mantine/core";
import { L2KText } from "../Typography";
import "@mantine/tiptap/styles.css";
import "./forms.css";

type PromptRequest = {
  title: string;
  label: string;
  placeholder?: string;
  initialValue: string;
  multiline: boolean;
  submitLabel?: string;
  validate?: (v: string) => string | null;
  onConfirm: (value: string) => void;
};

function EmbedPromptModal({
  request,
  onClose,
}: {
  request: PromptRequest | null;
  onClose: () => void;
}) {
  const [value, setValue] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setValue(request?.initialValue ?? "");
    setError(null);
  }, [request]);

  if (!request) return null;

  const handleSubmit = () => {
    const trimmed = value.trim();
    if (!trimmed) {
      setError("Value can't be empty.");
      return;
    }
    const validationError = request.validate?.(trimmed) ?? null;
    if (validationError) {
      setError(validationError);
      return;
    }
    request.onConfirm(trimmed);
    onClose();
  };

  return (
    <Modal
      opened
      onClose={onClose}
      title={request.title}
      size="lg"
      centered
      styles={{
        content: { background: "#0f0f14" },
        header: {
          background: "#0f0f14",
          borderBottom: "1px solid rgba(255, 152, 0, 0.2)",
        },
        title: {
          color: "#F0F0F0",
          fontFamily: "'Big Shoulders Display', sans-serif",
          fontWeight: 600,
          fontSize: 20,
        },
      }}
    >
      <Stack gap="md">
        <Textarea
          label={request.label}
          placeholder={request.placeholder}
          value={value}
          onChange={(e) => {
            setValue(e.currentTarget.value);
            if (error) setError(null);
          }}
          onKeyDown={(e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
              e.preventDefault();
              handleSubmit();
            }
          }}
          autosize
          minRows={request.multiline ? 6 : 1}
          maxRows={20}
          styles={{
            label: { color: "#F0F0F0", marginBottom: 6, fontWeight: 500 },
            input: {
              background: "rgba(255, 255, 255, 0.03)",
              borderColor: "rgba(255, 152, 0, 0.25)",
              color: "#F0F0F0",
              fontFamily: request.multiline
                ? "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace"
                : "'Public Sans', sans-serif",
              fontSize: request.multiline ? 13 : 14,
              lineHeight: 1.6,
            },
          }}
          autoFocus
        />
        {error && (
          <Alert color="red" radius="md" variant="light">
            {error}
          </Alert>
        )}
        <Group justify="space-between" align="center">
          <L2KText size="xs" c="dimmed">
            {request.multiline
              ? "Cmd/Ctrl + Enter to save · Esc to cancel"
              : "Enter to save · Esc to cancel"}
          </L2KText>
          <Group gap="sm">
            <Button variant="subtle" color="gray" onClick={onClose}>
              Cancel
            </Button>
            <Button color="orange" onClick={handleSubmit}>
              {request.submitLabel ?? "Save"}
            </Button>
          </Group>
        </Group>
      </Stack>
    </Modal>
  );
}

// Multi-field prompt for nodes with more than one editable attr (callout, quote, postimage).
// Field kinds: text (TextInput), textarea (multiline Textarea), select (dropdown).
type MultiFieldSpec = {
  name: string;
  label: string;
  kind: "text" | "textarea" | "select";
  placeholder?: string;
  options?: { value: string; label: string }[]; // required if kind === "select"
  required?: boolean;
};

type MultiFieldPromptRequest = {
  title: string;
  fields: MultiFieldSpec[];
  initialValues: Record<string, string>;
  submitLabel?: string;
  onConfirm: (values: Record<string, string>) => void;
};

function MultiFieldPromptModal({
  request,
  onClose,
}: {
  request: MultiFieldPromptRequest | null;
  onClose: () => void;
}) {
  const [values, setValues] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setValues(request?.initialValues ?? {});
    setError(null);
  }, [request]);

  if (!request) return null;

  const handleSubmit = () => {
    for (const field of request.fields) {
      if (field.required && !values[field.name]?.trim()) {
        setError(`${field.label} is required.`);
        return;
      }
    }
    request.onConfirm(values);
    onClose();
  };

  return (
    <Modal
      opened
      onClose={onClose}
      title={request.title}
      size="lg"
      centered
      styles={{
        content: { background: "#0f0f14" },
        header: {
          background: "#0f0f14",
          borderBottom: "1px solid rgba(255, 152, 0, 0.2)",
        },
        title: {
          color: "#F0F0F0",
          fontFamily: "'Big Shoulders Display', sans-serif",
          fontWeight: 600,
          fontSize: 20,
        },
      }}
    >
      <Stack gap="md">
        {request.fields.map((field) => {
          const common = {
            label: field.label,
            placeholder: field.placeholder,
            value: values[field.name] ?? "",
            styles: {
              label: { color: "#F0F0F0", marginBottom: 6, fontWeight: 500 },
              input: {
                background: "rgba(255, 255, 255, 0.03)",
                borderColor: "rgba(255, 152, 0, 0.25)",
                color: "#F0F0F0",
                fontFamily: "'Public Sans', sans-serif",
                fontSize: 14,
              },
            },
          };

          if (field.kind === "select") {
            return (
              <Select
                key={field.name}
                {...common}
                data={field.options ?? []}
                onChange={(v) => {
                  setValues((prev) => ({ ...prev, [field.name]: v ?? "" }));
                  if (error) setError(null);
                }}
                allowDeselect={false}
              />
            );
          }

          if (field.kind === "textarea") {
            return (
              <Textarea
                key={field.name}
                {...common}
                autosize
                minRows={4}
                maxRows={12}
                onChange={(e) => {
                  setValues((prev) => ({ ...prev, [field.name]: e.currentTarget.value }));
                  if (error) setError(null);
                }}
              />
            );
          }

          return (
            <TextInput
              key={field.name}
              {...common}
              onChange={(e) => {
                setValues((prev) => ({ ...prev, [field.name]: e.currentTarget.value }));
                if (error) setError(null);
              }}
            />
          );
        })}
        {error && (
          <Alert color="red" radius="md" variant="light">
            {error}
          </Alert>
        )}
        <Group justify="space-between" align="center">
          <L2KText size="xs" c="dimmed">
            Esc to cancel
          </L2KText>
          <Group gap="sm">
            <Button variant="subtle" color="gray" onClick={onClose}>
              Cancel
            </Button>
            <Button color="orange" onClick={handleSubmit}>
              {request.submitLabel ?? "Save"}
            </Button>
          </Group>
        </Group>
      </Stack>
    </Modal>
  );
}

// Legacy Webflow-imported posts parse into schema-adjacent ProseMirror nodes
// that trip ProseMirror's default join/split commands on Backspace/Enter/
// Delete. The error is thrown inside the keymap plugin BEFORE any transaction
// is dispatched, so wrapping dispatchTransaction doesn't catch it. Suppress
// the specific "contentMatchAt" error at the window level — the editor
// recovers on the next keystroke and the crash is cosmetic.
if (typeof window !== "undefined" && !(window as any).__l2kPmErrorSuppressor) {
  (window as any).__l2kPmErrorSuppressor = true;
  window.addEventListener("error", (event) => {
    if (event.error?.message?.includes("contentMatchAt on a node with invalid content")) {
      event.preventDefault();
      // eslint-disable-next-line no-console
      console.warn("[L2KRichTextEditor] suppressed ProseMirror schema error:", event.error.message);
    }
  });
}

export interface L2KRichTextEditorProps {
  value: string;
  onChange: (markdown: string) => void;
  label?: string;
  placeholder?: string;
  minHeight?: number;
}

// Safer Backspace/Delete/Enter that gracefully falls through when ProseMirror's
// default join/split throws on legacy Webflow-imported content. Higher priority
// than StarterKit so ours runs first; each strategy is try/catch guarded and
// falls through to the next on error.
const SafeKeymap = Extension.create({
  name: "l2kSafeKeymap",
  priority: 1000,
  addKeyboardShortcuts() {
    const attempt = (fn: () => boolean): boolean => {
      try { return fn(); } catch { return false; }
    };

    return {
      Backspace: ({ editor }) => {
        const { selection } = editor.state;
        const { from, empty } = selection;
        // 0. Selected atom node (click-selected callout/quote/embed) → drop it
        if (selection instanceof NodeSelection && attempt(() => editor.commands.deleteSelection())) return true;
        // 1. If there's a selection, deleteSelection is always safe
        if (!empty && attempt(() => editor.commands.deleteSelection())) return true;
        // 2. Try the normal joinBackward (works 99% of the time)
        if (attempt(() => editor.commands.joinBackward())) return true;
        // 3. Fallback: delete one char backwards without joining blocks
        if (from > 0 && attempt(() => editor.chain().deleteRange({ from: from - 1, to: from }).run())) return true;
        // 4. Last resort — consume the key so PM doesn't crash the editor
        return true;
      },
      Delete: ({ editor }) => {
        const { selection } = editor.state;
        const { from, to, empty } = selection;
        if (selection instanceof NodeSelection && attempt(() => editor.commands.deleteSelection())) return true;
        if (!empty && attempt(() => editor.commands.deleteSelection())) return true;
        if (attempt(() => editor.commands.joinForward())) return true;
        if (attempt(() => editor.chain().deleteRange({ from, to: to + 1 }).run())) return true;
        return true;
      },
      Enter: ({ editor }) => {
        if (attempt(() => editor.commands.splitBlock())) return true;
        if (attempt(() => editor.chain().insertContent("\n").run())) return true;
        return true;
      },
    };
  },
});

// YouTube nodes → <videoembed videoid="…" platform="youtube" /> on save.
// Matching atom/isolating/draggable settings to our other embed nodes so
// Backspace next to a video can't try to merge across the atom boundary.
// renderHTML is overridden (mirroring the upstream extension) so we can add
// a hover "×" delete button alongside the iframe.
// parseHTML also matches our own <videoembed> serialization so the tag
// round-trips instead of being HTML-escaped on the next save.
const YoutubeWithMarkdown = Youtube.extend({
  atom: true,
  isolating: true,
  selectable: true,
  draggable: false,
  parseHTML() {
    return [
      { tag: "div[data-youtube-video] iframe" },
      { tag: "videoembed[videoid]" },
    ];
  },
  addAttributes() {
    return {
      src: {
        default: null,
        parseHTML: (element: HTMLElement) => {
          if (element.tagName.toLowerCase() === "videoembed") {
            const videoid = element.getAttribute("videoid");
            return videoid ? `https://www.youtube.com/watch?v=${videoid}` : null;
          }
          const src = element.getAttribute("src") ?? "";
          return getAttributesFromYoutubeEmbedUrl(src)?.src ?? src ?? null;
        },
      },
      start: {
        default: 0,
        parseHTML: (element: HTMLElement) => {
          if (element.tagName.toLowerCase() === "videoembed") return 0;
          const src = element.getAttribute("src") ?? "";
          return Number(getAttributesFromYoutubeEmbedUrl(src)?.start ?? 0);
        },
      },
      width: { default: 640 },
      height: { default: 360 },
    };
  },
  renderHTML({ HTMLAttributes }) {
    const opts = this.options;
    const embedUrl = getEmbedUrlFromYoutubeUrl({
      url: HTMLAttributes.src,
      allowFullscreen: opts.allowFullscreen,
      autoplay: opts.autoplay,
      ccLanguage: opts.ccLanguage,
      ccLoadPolicy: opts.ccLoadPolicy,
      controls: opts.controls,
      disableKBcontrols: opts.disableKBcontrols,
      enableIFrameApi: opts.enableIFrameApi,
      endTime: opts.endTime,
      interfaceLanguage: opts.interfaceLanguage,
      ivLoadPolicy: opts.ivLoadPolicy,
      loop: opts.loop,
      modestBranding: opts.modestBranding,
      nocookie: opts.nocookie,
      origin: opts.origin,
      playlist: opts.playlist,
      progressBarColor: opts.progressBarColor,
      startAt: HTMLAttributes.start || 0,
      rel: opts.rel,
    });

    return [
      "div",
      { "data-youtube-video": "", class: "l2k-rte-youtube" },
      [
        "iframe",
        {
          src: embedUrl,
          width: opts.width,
          height: opts.height,
          allowfullscreen: opts.allowFullscreen ? "" : undefined,
          frameborder: 0,
          allow: "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture",
        },
      ],
      ["button", { type: "button", class: "l2k-rte-close", "aria-label": "Delete video", contenteditable: "false" }, "×"],
    ];
  },
  renderMarkdown(node) {
    const src: string = node?.attrs?.src ?? "";
    const id = extractYouTubeId(src);
    if (!id) return "";
    return `\n\n<videoembed videoid="${id}" platform="youtube"></videoembed>\n\n`;
  },
});

// Generic embed node → <embed url="…" /> MDX tag (Vimeo, Spotify, Loom, etc.)
const GenericEmbed = Node.create({
  name: "genericEmbed",
  group: "block",
  atom: true,
  isolating: true,
  selectable: true,
  draggable: false,
  addAttributes() {
    return {
      url: {
        default: "",
        parseHTML: (el) =>
          el.getAttribute("url") || el.getAttribute("data-generic-embed-url") || "",
      },
    };
  },
  parseHTML() {
    return [{ tag: "embed[url]" }, { tag: "div[data-generic-embed-url]" }];
  },
  renderHTML({ HTMLAttributes }) {
    const url = HTMLAttributes.url ?? "";
    return [
      "div",
      mergeAttributes(
        { "data-generic-embed-url": url, class: "l2k-rte-embed" },
        HTMLAttributes,
      ),
      ["span", { class: "l2k-rte-embed-label" }, `🔗 Embed — ${url}`],
      ["button", { type: "button", class: "l2k-rte-close", "aria-label": "Delete embed", contenteditable: "false" }, "×"],
    ];
  },
  renderMarkdown(node) {
    return `\n\n<embed url="${node?.attrs?.url ?? ""}"></embed>\n\n`;
  },
  addCommands() {
    return {
      setGenericEmbed:
        (attrs: { url: string }) =>
        ({ commands }) =>
          commands.insertContent({ type: this.name, attrs }),
    } as any;
  },
});

// Raw HTML embed node — user pastes any iframe snippet; we emit it verbatim
// into the markdown so rehype-raw renders it on the public side.
const RawEmbed = Node.create({
  name: "rawEmbed",
  group: "block",
  atom: true,
  isolating: true,
  selectable: true,
  draggable: false,
  addAttributes() {
    return {
      html: {
        default: "",
        parseHTML: (el) => el.getAttribute("data-raw-embed-html") || "",
      },
    };
  },
  parseHTML() {
    return [{ tag: "div[data-raw-embed]" }];
  },
  renderHTML({ HTMLAttributes }) {
    return [
      "div",
      mergeAttributes(
        { "data-raw-embed": "true", class: "l2k-rte-raw-embed" },
        HTMLAttributes,
      ),
      ["span", { class: "l2k-rte-embed-label" }, "🧩 Custom HTML embed"],
      ["button", { type: "button", class: "l2k-rte-close", "aria-label": "Delete embed", contenteditable: "false" }, "×"],
    ];
  },
  renderMarkdown(node) {
    const html: string = node?.attrs?.html ?? "";
    return `\n\n${html}\n\n`;
  },
  addCommands() {
    return {
      setRawEmbed:
        (attrs: { html: string }) =>
        ({ commands }) =>
          commands.insertContent({ type: this.name, attrs }),
    } as any;
  },
});

// Custom SoundCloud node — atomic block, serialises to <soundcloudembed url="…" />
const SoundCloud = Node.create({
  name: "soundcloud",
  group: "block",
  atom: true,
  isolating: true,
  selectable: true,
  draggable: false,
  addAttributes() {
    return {
      url: {
        default: "",
        parseHTML: (el) =>
          el.getAttribute("url") || el.getAttribute("data-soundcloud-url") || "",
      },
    };
  },
  parseHTML() {
    return [{ tag: "soundcloudembed" }, { tag: "div[data-soundcloud-url]" }];
  },
  renderHTML({ HTMLAttributes }) {
    const url = HTMLAttributes.url ?? "";
    return [
      "div",
      mergeAttributes(
        { "data-soundcloud-url": url, class: "l2k-rte-soundcloud" },
        HTMLAttributes,
      ),
      ["span", { class: "l2k-rte-embed-label" }, `♪ SoundCloud embed — ${url}`],
      ["button", { type: "button", class: "l2k-rte-close", "aria-label": "Delete embed", contenteditable: "false" }, "×"],
    ];
  },
  renderMarkdown(node) {
    return `\n\n<soundcloudembed url="${node?.attrs?.url ?? ""}"></soundcloudembed>\n\n`;
  },
  addCommands() {
    return {
      setSoundcloud:
        (attrs: { url: string }) =>
        ({ commands }) =>
          commands.insertContent({ type: this.name, attrs }),
    } as any;
  },
});

// Custom Callout node — atomic block, round-trips as <callout type="…" title="…">body</callout>
// MVP: body text is stored as an attribute (not editable child content). Rich body
// content (nested bold/links/lists inside a callout) would require content: "block+"
// and a more complex ProseMirror serializer — deferred to a follow-up.
const Callout = Node.create({
  name: "callout",
  group: "block",
  atom: true,
  isolating: true,
  selectable: true,
  draggable: false,
  addAttributes() {
    return {
      type: {
        default: "info",
        parseHTML: (el) => el.getAttribute("type") || el.getAttribute("data-callout-type") || "info",
      },
      title: {
        default: "",
        parseHTML: (el) => el.getAttribute("title") || el.getAttribute("data-callout-title") || "",
      },
      body: {
        default: "",
        parseHTML: (el) => el.getAttribute("data-callout-body") || el.textContent || "",
      },
    };
  },
  parseHTML() {
    return [{ tag: "callout" }, { tag: "div[data-callout-type]" }];
  },
  renderHTML({ HTMLAttributes }) {
    const type = HTMLAttributes.type ?? "info";
    const title = HTMLAttributes.title ?? "";
    const body = HTMLAttributes.body ?? "";
    const icon = type === "warning" ? "⚠️" : type === "success" ? "✅" : type === "tip" ? "💡" : "ℹ️";
    const label = title ? `${icon} ${type} — ${title}` : `${icon} ${type} callout`;
    return [
      "div",
      mergeAttributes(
        {
          "data-callout-type": type,
          "data-callout-title": title,
          "data-callout-body": body,
          class: "l2k-rte-callout",
        },
        HTMLAttributes,
      ),
      ["span", { class: "l2k-rte-embed-label" }, label],
      ["span", { class: "l2k-rte-embed-preview" }, body.slice(0, 120) + (body.length > 120 ? "…" : "")],
      ["button", { type: "button", class: "l2k-rte-close", "aria-label": "Delete callout", contenteditable: "false" }, "×"],
    ];
  },
  renderMarkdown(node) {
    const type = node?.attrs?.type ?? "info";
    const title = node?.attrs?.title ?? "";
    const body = node?.attrs?.body ?? "";
    const titleAttr = title ? ` title="${title.replace(/"/g, "&quot;")}"` : "";
    return `\n\n<callout type="${type}"${titleAttr}>${body}</callout>\n\n`;
  },
  addCommands() {
    return {
      setCallout:
        (attrs: { type?: string; title?: string; body: string }) =>
        ({ commands }: any) =>
          commands.insertContent({ type: this.name, attrs: { type: "info", title: "", ...attrs } }),
    } as any;
  },
});

// Custom Quote node — atomic block, round-trips as <quote author="…" role="…">text</quote>
const Quote = Node.create({
  name: "pullQuote",
  group: "block",
  atom: true,
  isolating: true,
  selectable: true,
  draggable: false,
  addAttributes() {
    return {
      author: {
        default: "",
        parseHTML: (el) => el.getAttribute("author") || el.getAttribute("data-quote-author") || "",
      },
      role: {
        default: "",
        parseHTML: (el) => el.getAttribute("role") || el.getAttribute("data-quote-role") || "",
      },
      text: {
        default: "",
        parseHTML: (el) => el.getAttribute("data-quote-text") || el.textContent || "",
      },
    };
  },
  parseHTML() {
    return [{ tag: "quote" }, { tag: "div[data-quote-text]" }];
  },
  renderHTML({ HTMLAttributes }) {
    const author = HTMLAttributes.author ?? "";
    const text = HTMLAttributes.text ?? "";
    const label = author ? `❝ Quote — ${author}` : "❝ Pull quote";
    return [
      "div",
      mergeAttributes(
        {
          "data-quote-author": author,
          "data-quote-role": HTMLAttributes.role ?? "",
          "data-quote-text": text,
          class: "l2k-rte-quote",
        },
        HTMLAttributes,
      ),
      ["span", { class: "l2k-rte-embed-label" }, label],
      ["span", { class: "l2k-rte-embed-preview" }, text.slice(0, 120) + (text.length > 120 ? "…" : "")],
      ["button", { type: "button", class: "l2k-rte-close", "aria-label": "Delete quote", contenteditable: "false" }, "×"],
    ];
  },
  renderMarkdown(node) {
    const author = node?.attrs?.author ?? "";
    const role = node?.attrs?.role ?? "";
    const text = node?.attrs?.text ?? "";
    const authorAttr = author ? ` author="${author.replace(/"/g, "&quot;")}"` : "";
    const roleAttr = role ? ` role="${role.replace(/"/g, "&quot;")}"` : "";
    return `\n\n<quote${authorAttr}${roleAttr}>${text}</quote>\n\n`;
  },
  addCommands() {
    return {
      setPullQuote:
        (attrs: { author?: string; role?: string; text: string }) =>
        ({ commands }: any) =>
          commands.insertContent({ type: this.name, attrs: { author: "", role: "", ...attrs } }),
    } as any;
  },
});

// Custom PostImage node — atomic block, round-trips as <postimage src="…" alt="…" caption="…" />
const PostImage = Node.create({
  name: "postImage",
  group: "block",
  atom: true,
  isolating: true,
  selectable: true,
  draggable: false,
  addAttributes() {
    return {
      src: {
        default: "",
        parseHTML: (el) => el.getAttribute("src") || el.getAttribute("data-postimage-src") || "",
      },
      alt: {
        default: "",
        parseHTML: (el) => el.getAttribute("alt") || el.getAttribute("data-postimage-alt") || "",
      },
      caption: {
        default: "",
        parseHTML: (el) => el.getAttribute("caption") || el.getAttribute("data-postimage-caption") || "",
      },
    };
  },
  parseHTML() {
    return [{ tag: "postimage" }, { tag: "div[data-postimage-src]" }];
  },
  renderHTML({ HTMLAttributes }) {
    const src = HTMLAttributes.src ?? "";
    const alt = HTMLAttributes.alt ?? "";
    const caption = HTMLAttributes.caption ?? "";
    return [
      "div",
      mergeAttributes(
        {
          "data-postimage-src": src,
          "data-postimage-alt": alt,
          "data-postimage-caption": caption,
          class: "l2k-rte-postimage",
        },
        HTMLAttributes,
      ),
      ["span", { class: "l2k-rte-embed-label" }, `🖼 Post image — ${alt || src}`],
      caption ? ["span", { class: "l2k-rte-embed-preview" }, caption] : ["span", {}, ""],
      ["button", { type: "button", class: "l2k-rte-close", "aria-label": "Delete image", contenteditable: "false" }, "×"],
    ];
  },
  renderMarkdown(node) {
    const src = node?.attrs?.src ?? "";
    const alt = node?.attrs?.alt ?? "";
    const caption = node?.attrs?.caption ?? "";
    const captionAttr = caption ? ` caption="${caption.replace(/"/g, "&quot;")}"` : "";
    return `\n\n<postimage src="${src}" alt="${alt.replace(/"/g, "&quot;")}"${captionAttr} />\n\n`;
  },
  addCommands() {
    return {
      setPostImage:
        (attrs: { src: string; alt: string; caption?: string }) =>
        ({ commands }: any) =>
          commands.insertContent({ type: this.name, attrs: { caption: "", ...attrs } }),
    } as any;
  },
});

function extractYouTubeId(url: string): string | null {
  const m = url.match(
    /(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{11})/,
  );
  return m ? m[1] : null;
}

const URL_REQUIRED = (v: string) =>
  /^https?:\/\//i.test(v) ? null : "URL must start with http:// or https://";
const SOUNDCLOUD_ONLY = (v: string) =>
  /soundcloud\.com\//i.test(v) ? null : "That doesn't look like a SoundCloud URL.";
const YOUTUBE_ONLY = (v: string) =>
  extractYouTubeId(v) ? null : "Couldn't recognise that as a YouTube URL.";
const HTML_ONLY = (v: string) =>
  v.trim().startsWith("<") ? null : "Expected HTML — must start with <";

export function L2KRichTextEditor({
  value,
  onChange,
  label,
  placeholder,
  minHeight = 480,
}: L2KRichTextEditorProps) {
  const [promptReq, setPromptReq] = useState<PromptRequest | null>(null);
  const closePrompt = () => setPromptReq(null);
  const [multiReq, setMultiReq] = useState<MultiFieldPromptRequest | null>(null);
  const closeMulti = () => setMultiReq(null);
  const [sourceMode, setSourceMode] = useState(false);

  const CALLOUT_TYPES = [
    { value: "info", label: "Info (blue)" },
    { value: "warning", label: "Warning (red)" },
    { value: "success", label: "Success (green)" },
    { value: "tip", label: "Tip (yellow)" },
  ];

  const editor = useEditor({
    extensions: [
      SafeKeymap,
      StarterKit.configure({ link: false }),
      Link,
      Highlight,
      YoutubeWithMarkdown.configure({
        controls: false,
        nocookie: true,
        width: 640,
        height: 360,
      }),
      SoundCloud,
      GenericEmbed,
      RawEmbed,
      Callout,
      Quote,
      PostImage,
      Markdown,
    ],
    content: value,
    contentType: "markdown",
    shouldRerenderOnTransaction: true,
    onUpdate: ({ editor }) => {
      onChange(editor.getMarkdown());
    },
    editorProps: {
      // Click on the "×" delete button of an embed placeholder → remove the node.
      handleClickOn(view, _pos, node, nodePos, event) {
        const target = event.target as HTMLElement | null;
        if (target?.classList?.contains("l2k-rte-close")) {
          event.preventDefault();
          view.dispatch(view.state.tr.delete(nodePos, nodePos + node.nodeSize));
          return true;
        }
        return false;
      },
      // Double-click any embed placeholder to change its URL (or HTML).
      handleDoubleClickOn(_view, _pos, node, nodePos) {
        if (!editor) return false;
        const t = node.type.name;

        const updateAttrs = (attrs: Record<string, unknown>) => {
          editor
            .chain()
            .focus()
            .command(({ tr }) => {
              tr.setNodeMarkup(nodePos, undefined, { ...node.attrs, ...attrs });
              return true;
            })
            .run();
        };

        if (t === "soundcloud") {
          setPromptReq({
            title: "Edit SoundCloud embed",
            label: "SoundCloud URL",
            initialValue: String(node.attrs.url ?? ""),
            multiline: false,
            validate: SOUNDCLOUD_ONLY,
            onConfirm: (v) => updateAttrs({ url: v }),
          });
          return true;
        }

        if (t === "genericEmbed") {
          setPromptReq({
            title: "Edit embed URL",
            label: "URL",
            initialValue: String(node.attrs.url ?? ""),
            multiline: false,
            validate: URL_REQUIRED,
            onConfirm: (v) => updateAttrs({ url: v }),
          });
          return true;
        }

        if (t === "youtube") {
          setPromptReq({
            title: "Edit YouTube embed",
            label: "YouTube URL",
            initialValue: String(node.attrs.src ?? ""),
            multiline: false,
            validate: YOUTUBE_ONLY,
            onConfirm: (v) => updateAttrs({ src: v }),
          });
          return true;
        }

        if (t === "rawEmbed") {
          setPromptReq({
            title: "Edit raw HTML embed",
            label: "Embed HTML",
            placeholder: "<iframe src=\"…\" …></iframe>",
            initialValue: String(node.attrs.html ?? ""),
            multiline: true,
            validate: HTML_ONLY,
            onConfirm: (v) => updateAttrs({ html: v }),
          });
          return true;
        }

        return false;
      },
    },
    // Safety net: some legacy Webflow-imported content parses into
    // schema-adjacent nodes that ProseMirror's default Backspace handler
    // can't merge cleanly, throwing "Called contentMatchAt on a node with
    // invalid content". Wrap the transaction dispatch so a bad key press
    // is a no-op instead of nuking the whole editor.
    onCreate({ editor }) {
      const view = editor.view;
      const originalDispatch = view.props.dispatchTransaction?.bind(view);
      view.setProps({
        dispatchTransaction(tr) {
          try {
            if (originalDispatch) originalDispatch(tr);
            else view.updateState(view.state.apply(tr));
          } catch (err) {
            console.warn("[L2KRichTextEditor] blocked invalid transaction", err);
          }
        },
      });
    },
  });

  useEffect(() => {
    if (!editor) return;
    const current = editor.getMarkdown();
    if (value !== current) {
      editor.commands.setContent(value, { contentType: "markdown", emitUpdate: false });
    }
  }, [value, editor]);

  const insertYouTube = () => {
    if (!editor) return;
    setPromptReq({
      title: "Insert YouTube video",
      label: "YouTube URL",
      placeholder: "https://youtube.com/watch?v=…",
      initialValue: "",
      multiline: false,
      submitLabel: "Insert",
      validate: YOUTUBE_ONLY,
      onConfirm: (url) => {
        editor.chain().focus().setYoutubeVideo({ src: url }).run();
      },
    });
  };

  const insertSoundCloud = () => {
    if (!editor) return;
    setPromptReq({
      title: "Insert SoundCloud embed",
      label: "SoundCloud track or playlist URL",
      placeholder: "https://soundcloud.com/…",
      initialValue: "",
      multiline: false,
      submitLabel: "Insert",
      validate: SOUNDCLOUD_ONLY,
      onConfirm: (url) => {
        (editor.chain().focus() as any).setSoundcloud({ url }).run();
      },
    });
  };

  const insertGenericEmbed = () => {
    if (!editor) return;
    setPromptReq({
      title: "Insert embed",
      label: "URL",
      placeholder: "Vimeo, Spotify, Loom, CodePen, Twitch, Google Slides, or any embeddable page",
      initialValue: "",
      multiline: false,
      submitLabel: "Insert",
      validate: URL_REQUIRED,
      onConfirm: (url) => {
        (editor.chain().focus() as any).setGenericEmbed({ url }).run();
      },
    });
  };

  const insertRawEmbed = () => {
    if (!editor) return;
    setPromptReq({
      title: "Insert raw HTML embed",
      label: "Embed HTML",
      placeholder: "<iframe src=\"…\" width=\"640\" height=\"360\"></iframe>",
      initialValue: "",
      multiline: true,
      submitLabel: "Insert",
      validate: HTML_ONLY,
      onConfirm: (html) => {
        (editor.chain().focus() as any).setRawEmbed({ html }).run();
      },
    });
  };

  const insertCallout = () => {
    if (!editor) return;
    setMultiReq({
      title: "Insert callout",
      submitLabel: "Insert",
      initialValues: { type: "info", title: "", body: "" },
      fields: [
        { name: "type", label: "Type", kind: "select", options: CALLOUT_TYPES, required: true },
        { name: "title", label: "Title (optional)", kind: "text", placeholder: "e.g. Heads up" },
        { name: "body", label: "Body", kind: "textarea", placeholder: "Callout text", required: true },
      ],
      onConfirm: (v) => {
        (editor.chain().focus() as any)
          .setCallout({ type: v.type, title: v.title, body: v.body })
          .run();
      },
    });
  };

  const insertQuote = () => {
    if (!editor) return;
    setMultiReq({
      title: "Insert pull quote",
      submitLabel: "Insert",
      initialValues: { author: "", role: "", text: "" },
      fields: [
        { name: "text", label: "Quote text", kind: "textarea", placeholder: "The quote itself", required: true },
        { name: "author", label: "Author (optional)", kind: "text", placeholder: "e.g. Charles Ogar" },
        { name: "role", label: "Byline / role (optional)", kind: "text", placeholder: "e.g. Instructor" },
      ],
      onConfirm: (v) => {
        (editor.chain().focus() as any)
          .setPullQuote({ author: v.author, role: v.role, text: v.text })
          .run();
      },
    });
  };

  const insertPostImage = () => {
    if (!editor) return;
    setMultiReq({
      title: "Insert post image",
      submitLabel: "Insert",
      initialValues: { src: "", alt: "", caption: "" },
      fields: [
        { name: "src", label: "Image URL", kind: "text", placeholder: "https://…", required: true },
        { name: "alt", label: "Alt text", kind: "text", placeholder: "Describe the image", required: true },
        { name: "caption", label: "Caption (optional)", kind: "text", placeholder: "Shown under the image" },
      ],
      onConfirm: (v) => {
        (editor.chain().focus() as any)
          .setPostImage({ src: v.src, alt: v.alt, caption: v.caption })
          .run();
      },
    });
  };

  return (
    <div className="l2k-rte">
      {label && (
        <L2KText size="sm" mb={6} style={{ fontWeight: 500 }}>
          {label}
        </L2KText>
      )}
      <RichTextEditor
        editor={editor}
        styles={{
          root: {
            background: "rgba(255, 255, 255, 0.03)",
            borderColor: "rgba(255, 152, 0, 0.2)",
          },
          toolbar: {
            background: "rgba(20, 20, 28, 0.6)",
            borderBottomColor: "rgba(255, 152, 0, 0.2)",
          },
          content: {
            background: "transparent",
            color: "#F0F0F0",
            minHeight,
            fontFamily: "'Public Sans', sans-serif",
            fontSize: 15,
            lineHeight: 1.7,
          },
        }}
      >
        <RichTextEditor.Toolbar sticky stickyOffset={0}>
          <RichTextEditor.ControlsGroup>
            <RichTextEditor.Bold />
            <RichTextEditor.Italic />
            <RichTextEditor.Underline />
            <RichTextEditor.Strikethrough />
            <RichTextEditor.Highlight />
            <RichTextEditor.Code />
          </RichTextEditor.ControlsGroup>

          <RichTextEditor.ControlsGroup>
            <RichTextEditor.H1 />
            <RichTextEditor.H2 />
            <RichTextEditor.H3 />
            <RichTextEditor.H4 />
          </RichTextEditor.ControlsGroup>

          <RichTextEditor.ControlsGroup>
            <RichTextEditor.BulletList />
            <RichTextEditor.OrderedList />
            <RichTextEditor.Blockquote />
            <RichTextEditor.Hr />
            <RichTextEditor.CodeBlock />
          </RichTextEditor.ControlsGroup>

          <RichTextEditor.ControlsGroup>
            <RichTextEditor.Link />
            <RichTextEditor.Unlink />
            <RichTextEditor.Control
              onClick={insertYouTube}
              aria-label="Insert YouTube video"
              title="Insert YouTube video"
            >
              <IconBrandYoutube size={16} stroke={1.5} />
            </RichTextEditor.Control>
            <RichTextEditor.Control
              onClick={insertSoundCloud}
              aria-label="Insert SoundCloud embed"
              title="Insert SoundCloud embed"
            >
              <IconBrandSoundcloud size={16} stroke={1.5} />
            </RichTextEditor.Control>
            <RichTextEditor.Control
              onClick={insertGenericEmbed}
              aria-label="Insert embed by URL"
              title="Insert embed by URL (Vimeo, Spotify, Loom, CodePen, …)"
            >
              <IconWorld size={16} stroke={1.5} />
            </RichTextEditor.Control>
            <RichTextEditor.Control
              onClick={insertRawEmbed}
              aria-label="Insert raw HTML embed"
              title="Insert raw HTML (paste an iframe snippet)"
            >
              <IconCode size={16} stroke={1.5} />
            </RichTextEditor.Control>
          </RichTextEditor.ControlsGroup>

          <RichTextEditor.ControlsGroup>
            <RichTextEditor.Control
              onClick={insertCallout}
              aria-label="Insert callout"
              title="Insert callout (info, warning, success, tip)"
            >
              <IconAlertTriangle size={16} stroke={1.5} />
            </RichTextEditor.Control>
            <RichTextEditor.Control
              onClick={insertQuote}
              aria-label="Insert pull quote"
              title="Insert pull quote"
            >
              <IconQuote size={16} stroke={1.5} />
            </RichTextEditor.Control>
            <RichTextEditor.Control
              onClick={insertPostImage}
              aria-label="Insert post image"
              title="Insert post image with caption"
            >
              <IconPhoto size={16} stroke={1.5} />
            </RichTextEditor.Control>
          </RichTextEditor.ControlsGroup>

          <RichTextEditor.ControlsGroup>
            <RichTextEditor.Undo />
            <RichTextEditor.Redo />
            <RichTextEditor.Control
              onClick={() => setSourceMode((v) => !v)}
              aria-label={sourceMode ? "Show rich text editor" : "Show markdown source"}
              title={sourceMode ? "Show rich text editor" : "Show markdown source"}
              active={sourceMode}
            >
              <IconMarkdown size={16} stroke={1.5} />
            </RichTextEditor.Control>
          </RichTextEditor.ControlsGroup>
        </RichTextEditor.Toolbar>

        {sourceMode ? (
          <Textarea
            value={value}
            onChange={(e) => onChange(e.currentTarget.value)}
            autosize
            minRows={Math.max(12, Math.floor(minHeight / 24))}
            styles={{
              input: {
                background: "transparent",
                border: "none",
                borderRadius: 0,
                color: "#F0F0F0",
                fontFamily:
                  "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                fontSize: 13,
                lineHeight: 1.6,
                minHeight,
                padding: 16,
              },
            }}
          />
        ) : (
          <RichTextEditor.Content
            data-placeholder={placeholder}
            style={{ minHeight }}
          />
        )}
      </RichTextEditor>
      <EmbedPromptModal request={promptReq} onClose={closePrompt} />
      <MultiFieldPromptModal request={multiReq} onClose={closeMulti} />
    </div>
  );
}

export default L2KRichTextEditor;
